import * as THREE from "three";
import type { CollisionWorld, TacticalKind, Vec3 } from "@rietz/shared";

import { TAC_LAMP_COLOR, TAC_LASER_COLOR } from "../render/palette.js";
import { makeSoftDotTexture } from "./fx.js";

/**
 * The LOCAL player's mounted lamp and laser. Not part of the viewmodel: the
 * first-person gun lives in its OWN scene, and a light parented there would
 * light the receiver and not the barn wall. So both effects live in the WORLD
 * scene, driven from the EYE every frame; the laser's ORIGIN is the barrel tip
 * mapped into the world camera's frame (`Viewmodel.muzzleInView`), so the beam
 * leaves the gun where it is drawn and lands where the eye's cast says.
 *
 * Conventions from `game/fx.ts`: preallocated, `update` allocates nothing, the
 * beam is additive/unfogged so a red line reads through the haze. No DOM.
 */

/** Lamp throw, metres. Beyond this the cone is off entirely — a torch that lit
 *  the far hillside would be a searchlight. */
const LAMP_DISTANCE_M = 35;
/** Cone half-angle, radians (~18°). A hand torch, not a floodlight. */
const LAMP_ANGLE_RAD = 0.32;
/** Soft edge, 0..1. Anything crisper reads as a projected texture. */
const LAMP_PENUMBRA = 0.45;
/**
 * Candela, with `decay` 1 rather than the physical 2 — an honest inverse-square
 * torch is a bright disc at three metres and nothing at twenty.
 */
const LAMP_INTENSITY = 4;
const LAMP_DECAY = 1;

/** How far the laser is cast before it gives up and stops in mid-air. */
const LASER_RANGE_M = 100;
/** Beam thickness, metres. A line, not a rod. */
const BEAM_WIDTH_M = 0.008;
/**
 * Beam opacity, deliberately faint: THE DOT IS THE READ. The beam exists so
 * others can see it (`render/character.ts`) and so the player can tell the
 * diode is on.
 */
const BEAM_OPACITY = 0.25;

/** Dot size at the muzzle, and how much it grows per metre of range, so a mark
 *  at sixty metres is still a few pixels rather than a memory. */
const DOT_BASE_M = 0.03;
const DOT_PER_M = 0.004;

export interface TacticalFxOptions {
  /** The WORLD scene — see the header for why it cannot be the viewmodel's. */
  scene: THREE.Scene;
  /** Getter: the collision world is swapped on every map change, exactly as
   *  `game/bullets.ts` takes it. */
  world: () => CollisionWorld;
}

export class TacticalFx {
  private readonly o: TacticalFxOptions;

  // --- The lamp -------------------------------------------------------------
  private readonly lamp: THREE.SpotLight;
  /**
   * A `SpotLight` aims at an object, so the target is an empty node parked 30 m
   * down the view axis each frame. It must be IN the scene — three reads its
   * world matrix.
   */
  private readonly lampTarget = new THREE.Object3D();

  // --- The laser ------------------------------------------------------------
  /**
   * Modelled along −Z and one metre long: facing is one `setFromUnitVectors`,
   * length is `scale.z`.
   */
  private readonly beamGeo: THREE.BoxGeometry;
  private readonly beamMat: THREE.MeshBasicMaterial;
  private readonly beam: THREE.Mesh;
  private readonly dotTexture: THREE.Texture | null;
  private readonly dotMat: THREE.SpriteMaterial;
  private readonly dot: THREE.Sprite;

  // --- Scratch. `update` runs every frame and may not allocate --------------
  private readonly sEye = new THREE.Vector3();
  private readonly sDir = new THREE.Vector3();
  private readonly sOrigin = new THREE.Vector3();
  private readonly sEnd = new THREE.Vector3();
  /** Unit vector from the beam's muzzle origin to its far end — which is NOT
   *  the view direction, because the two do not start in the same place. */
  private readonly sBeam = new THREE.Vector3();
  private readonly sQuat = new THREE.Quaternion();
  /** The axis the beam box is modelled along; constant, never written to. */
  private readonly sAxis = new THREE.Vector3(0, 0, -1);
  /** `raycast` takes plain `Vec3`s, so the two ends of the cast are kept as
   *  plain objects rather than converted per frame. */
  private readonly rayFrom: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly rayDir: Vec3 = { x: 0, y: 0, z: 0 };

  constructor(opts: TacticalFxOptions) {
    this.o = opts;

    // NO SHADOWS: a casting spot light is a second render of the world per
    // frame,
    // and light through a wall is the same honest lie the muzzle flash tells.
    this.lamp = new THREE.SpotLight(
      TAC_LAMP_COLOR,
      LAMP_INTENSITY,
      LAMP_DISTANCE_M,
      LAMP_ANGLE_RAD,
      LAMP_PENUMBRA,
      LAMP_DECAY,
    );
    this.lamp.castShadow = false;
    this.lamp.name = "tac:lamp";
    this.lamp.visible = false;
    this.lampTarget.name = "tac:lampTarget";
    this.lamp.target = this.lampTarget;
    opts.scene.add(this.lamp);
    opts.scene.add(this.lampTarget);

    this.beamGeo = new THREE.BoxGeometry(BEAM_WIDTH_M, BEAM_WIDTH_M, 1);
    this.beamMat = new THREE.MeshBasicMaterial({
      color: TAC_LASER_COLOR,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: BEAM_OPACITY,
      depthWrite: false,
      // Unlit and unfogged, exactly like a tracer: the beam IS a light source,
      // and haze that swallowed it would swallow it at the range it matters.
      fog: false,
    });
    this.beam = new THREE.Mesh(this.beamGeo, this.beamMat);
    this.beam.name = "tac:beam";
    // Stretched across the whole valley every frame; a bounding sphere computed
    // from the unit box would be permanently wrong.
    this.beam.frustumCulled = false;
    this.beam.renderOrder = 5;
    this.beam.visible = false;
    opts.scene.add(this.beam);

    // The same soft radial dot as the dust puffs — a bright centre with a halo,
    // not a square. `null` under plain node.
    this.dotTexture = makeSoftDotTexture();
    this.dotMat = new THREE.SpriteMaterial({
      color: TAC_LASER_COLOR,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      fog: false,
    });
    if (this.dotTexture !== null) this.dotMat.map = this.dotTexture;
    this.dot = new THREE.Sprite(this.dotMat);
    this.dot.name = "tac:dot";
    this.dot.visible = false;
    opts.scene.add(this.dot);
  }

  /**
   * Places this frame's light; once per rendered frame, always. `kind` is
   * `null` for every reason (nothing fitted, switch off, dead, empty hands) and
   * everything is hidden. `muzzle` is the barrel tip in WORLD space; only the
   * laser reads it.
   */
  update(eye: Vec3, dir: Vec3, muzzle: Vec3, kind: TacticalKind | null): void {
    this.lamp.visible = kind === "lamp";
    this.beam.visible = kind === "laser";
    this.dot.visible = kind === "laser";
    if (kind === null) return;

    this.sEye.set(eye.x, eye.y, eye.z);
    this.sDir.set(dir.x, dir.y, dir.z);

    if (kind === "lamp") {
      this.lamp.position.copy(this.sEye);
      // Well beyond the cone's `distance`: the target only sets the DIRECTION.
      this.lampTarget.position.copy(this.sEye).addScaledVector(this.sDir, 30);
      return;
    }

    // --- The laser --------------------------------------------------------
    this.sOrigin.set(muzzle.x, muzzle.y, muzzle.z);

    // Cast from the EYE, not the muzzle: the eye is guaranteed outside
    // geometry,
    // the barrel tip may be inside the corner being leaned round. The beam is
    // then drawn from the muzzle to that end point — a few centimetres of
    // parallax.
    this.rayFrom.x = this.sEye.x;
    this.rayFrom.y = this.sEye.y;
    this.rayFrom.z = this.sEye.z;
    this.rayDir.x = this.sDir.x;
    this.rayDir.y = this.sDir.y;
    this.rayDir.z = this.sDir.z;
    const hit = this.o.world().raycast(this.rayFrom, this.rayDir, LASER_RANGE_M);
    const range = hit === null ? LASER_RANGE_M : hit.dist;
    this.sEnd.copy(this.sEye).addScaledVector(this.sDir, range);

    // Rotate the −Z box onto the beam direction, park it at the midpoint,
    // length in `scale.z`.
    const len = this.sBeam.copy(this.sEnd).sub(this.sOrigin).length();
    if (len <= 1e-4) {
      // The muzzle is in the wall. There is nothing sensible to draw, and a
      // zero-length beam would hand `setFromUnitVectors` a NaN.
      this.beam.visible = false;
    } else {
      this.sBeam.multiplyScalar(1 / len);
      this.sQuat.setFromUnitVectors(this.sAxis, this.sBeam);
      this.beam.quaternion.copy(this.sQuat);
      this.beam.position.copy(this.sOrigin).addScaledVector(this.sBeam, len * 0.5);
      this.beam.scale.set(1, 1, len);
    }

    // Grown with distance so the mark survives the range it is useful at — a
    // deliberate overcorrection.
    this.dot.position.copy(this.sEnd);
    this.dot.scale.setScalar(DOT_BASE_M + range * DOT_PER_M);
  }

  dispose(): void {
    this.lamp.removeFromParent();
    this.lamp.dispose();
    this.lampTarget.removeFromParent();
    this.beam.removeFromParent();
    this.beamGeo.dispose();
    this.beamMat.dispose();
    this.dot.removeFromParent();
    this.dotMat.dispose();
    this.dotTexture?.dispose();
  }
}
